import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Redirect } from "react-router-dom";
import { connect } from "react-redux";
import { Dispatch, bindActionCreators } from "redux";
import { Pivot, PivotItem } from "office-ui-fabric-react/lib/Pivot";
import EntityHeader from "../common/EntityHeader";
import "./ApprovalCenterPage.css";
import { ApplicationState } from "../../store";
import { Dictionary } from "../../entities/common";
import { loadApprovalSettings } from "../../store/ApprovalCenterStore";
import { ApprovalTabSettings, ApprovalTabSpec, buildSaveApprovalTabSetting } from "./approvalTabBase";
import { approvalTabSpecs } from "./approvalCenterTabs";
import * as TimesheetApprovalStore from "../../store/TimesheetApprovalStore";

type StateProps = {
    canUserApprove: boolean | undefined;
    visibleTabs: ApprovalTabSpec[];
    pendingCounts: Dictionary<number>;
    storedActiveTab?: string;
};

type ActionProps = {
    triggerLoadSettings: typeof loadApprovalSettings;
    triggerLoadPendingCount: (spec: ApprovalTabSpec) => void;
    saveSettings: typeof TimesheetApprovalStore.actionCreators.saveSettings;
};

type Props = StateProps & ActionProps;

const ApprovalCenterPage = (props: Props) => {
    const {
        canUserApprove,
        visibleTabs,
        pendingCounts,
        storedActiveTab,
        triggerLoadSettings,
        triggerLoadPendingCount,
        saveSettings
    } = props;
    const [selectedTab, setSelectedTab] = useState<string | undefined>(undefined);
    const saveSetting = useMemo(() => buildSaveApprovalTabSetting(saveSettings), [saveSettings]);
    const visibleTabsKey = visibleTabs.map(t => t.key).join("|");
    const isKnownKey = useCallback(
        (value: unknown): value is string => typeof value === "string" && visibleTabs.some(t => t.key === value),
        [visibleTabsKey]
    );

    useEffect(() => {
        if (!canUserApprove) {
            return;
        }
        triggerLoadSettings(visibleTabs.map(t => t.settings));
        for (const tab of visibleTabs) {
            triggerLoadPendingCount(tab);
        }
    }, [canUserApprove, visibleTabsKey, triggerLoadSettings, triggerLoadPendingCount]);

    if (canUserApprove === undefined) {
        return <></>;
    }

    if (!canUserApprove) {
        return <Redirect to="/error/403" />;
    }

    const fallbackKey = visibleTabs[0]?.key;
    const activeTab =
        selectedTab && isKnownKey(selectedTab) ? selectedTab : isKnownKey(storedActiveTab) ? storedActiveTab : fallbackKey;

    const handleTabChange = (key: string) => {
        if (!isKnownKey(key) || key === activeTab) {
            return;
        }
        setSelectedTab(key);
        saveSetting.approvalCenterActiveTab(key);
    };

    const activeTabSpec = visibleTabs.find(t => t.key === activeTab);

    return (
        <div className="approval-center entities-screen">
            <EntityHeader
                name="Approval Center"
                nameTitle="Approval Center"
                logo={<div className="logo approval-center-logo"></div>}
            />
            <div className="approval-center-tabs">
                <Pivot
                    selectedKey={activeTab}
                    onLinkClick={item => {
                        const key = item?.props.itemKey;
                        if (typeof key === "string") {
                            handleTabChange(key);
                        }
                    }}>
                    {visibleTabs.map(tab => (
                        <PivotItem
                            key={tab.key}
                            itemKey={tab.key}
                            headerText={`${tab.label} (${pendingCounts[tab.key] ?? 0})`}
                        />
                    ))}
                </Pivot>
            </div>
            <div className="entities-list">{activeTabSpec?.render()}</div>
        </div>
    );
};

const mapStateToProps = (state: ApplicationState): StateProps => {
    const visibleTabs = approvalTabSpecs.filter(t => t.isVisible(state));
    const pendingCounts: Dictionary<number> = {};
    for (const t of visibleTabs) {
        pendingCounts[t.key] = t.selectPendingCount(state);
    }
    const settings = state.timesheetApprovals.settings as ApprovalTabSettings | undefined;
    const canApprove = state.user.canApprove;
    return {
        canUserApprove: canApprove === undefined ? undefined : canApprove.timesheet || canApprove.stageTransition,
        visibleTabs,
        pendingCounts,
        storedActiveTab: settings?.approvalCenterActiveTab
    };
};

const mapDispatchToProps = (dispatch: Dispatch): ActionProps => {
    const bound = bindActionCreators(
        {
            loadApprovalSettings,
            saveSettings: TimesheetApprovalStore.actionCreators.saveSettings
        },
        dispatch
    );
    return {
        triggerLoadSettings: bound.loadApprovalSettings,
        saveSettings: bound.saveSettings,
        triggerLoadPendingCount: spec => bindActionCreators({ load: spec.loadPendingCount }, dispatch).load()
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ApprovalCenterPage);
